import { Axios } from '../../../../utils'
import {
  ItemResponseType,
  ListPaginationParams,
  ListResponseType,
} from '../../../../services/types'
import {
  FlourFilterPayload,
  TFlourAttributeGroup,
  TFlourTimeFilterValues,
  TFlourWithId,
  TFlourWithoutCategories,
  TProductWithDeliveries,
} from '../../../../types/models/Flours'
import { Delivery, EModule, TSpectralDistanceData } from '../../../../types'

type DeliveryFilterParams = {
  post_aging: boolean
}

type DeliveryPaginationParams = {
  offset: number
  limit: number
  total?: number
}

type ProductsWithDeliveriesPayload = {
  filters: DeliveryFilterParams
  pagination: DeliveryPaginationParams
  order_by?: string
}

export const useAnalyticsServices = (isBakerModule: boolean) => {
  const getProductDeliveryById = async (deliveryId: string) => {
    try {
      const response = await Axios.get<ItemResponseType<Delivery>>(
        `/${isBakerModule ? 'baker' : 'flour'}/deliveries/${deliveryId}`
      )
      return response.data
    } catch (error) {
      throw error
    }
  }

  const getProductsWithDeliveries = async (
    payload: ProductsWithDeliveriesPayload
  ) => {
    try {
      const response = await Axios.post<
        ListResponseType<TProductWithDeliveries>
      >(
        `/${isBakerModule ? 'baker' : 'flour'}/deliveries/products_with_deliveries`,
        payload
      )
      return response.data
    } catch (error) {
      throw error
    }
  }

  const getDeliveriesByArrivalDate = async (payload: {
    flour_product_id?: string
    baker_product_id?: string
    arrival_date: string
    filters: Record<string, boolean | string>
  }) => {
    const response = await Axios.post<ListResponseType<any>>(
      `/${isBakerModule ? 'baker' : 'flour'}/deliveries/by_arrival_date`,
      payload
    )
    return response.data
  }

  const getArrivalDatesByPeriod = async (payload: {
    flour_product_id?: string
    baker_product_id?: string
    period: TFlourTimeFilterValues
    filters: Record<string, boolean | string>
  }) => {
    try {
      const response = await Axios.post<
        ListResponseType<{
          flour_product_id?: string
          baker_product_id?: string
          arrival_dates: string[]
        }>
      >(
        `/${isBakerModule ? 'baker' : 'flour'}/deliveries/arrival_dates_by_period`,
        payload
      )
      return response.data
    } catch (error) {
      throw error
    }
  }

  const getUserAttributeFilters = async (
    post_aging: boolean,
    product_id: number
  ) => {
    try {
      const response = await Axios.get<TFlourAttributeGroup[]>(
        `/${isBakerModule ? 'baker' : 'flour'}/get-user-attribute-filters/${product_id}`,
        { params: { post_aging } }
      )
      return response.data
    } catch (error) {
      throw error
    }
  }

  const getProductsList = async (pagination?: ListPaginationParams) => {
    try {
      const response = await Axios.get<ListResponseType<TFlourWithId>>(
        `/${isBakerModule ? 'baker' : 'flour'}/products`,
        {
          params: pagination
            ? {
                offset: pagination.currentPage * pagination.itemsPerPage,
                limit: pagination.itemsPerPage,
              }
            : undefined,
        }
      )
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const getFlourProductById = async (id: string) => {
    try {
      const response = await Axios.get<
        ItemResponseType<TFlourWithoutCategories>
      >(`/${isBakerModule ? 'baker' : 'flour'}/products/${id}`)
      return response.data
    } catch (error: unknown) {
      throw error
    }
  }

  const getDeliverySpectralDistance = async (
    deliveryId: string,
    post_aging?: boolean
  ) => {
    try {
      const response = await Axios.post<
        ItemResponseType<TSpectralDistanceData>
      >(
        `/${isBakerModule ? 'baker' : 'flour'}/deliveries/spectral_distance/${deliveryId}`,
        {
          filters: [{ field: 'post_aging', value: post_aging, operator: 'eq' }],
        }
      )
      return response.data
    } catch (error) {
      throw error
    }
  }

  const addUserAttributeFilters = async (data: FlourFilterPayload) => {
    try {
      const response = await Axios.post<{ status: boolean; detail: string }>(
        `/${isBakerModule ? 'baker' : 'flour'}/check-user-attribute-filters`,
        data
      )
      return response.data
    } catch (error) {
      throw error
    }
  }

  const removeUserAttributeFilters = async (data: FlourFilterPayload) => {
    try {
      const response = await Axios.post<{ status: boolean; detail: string }>(
        `/${isBakerModule ? 'baker' : 'flour'}/uncheck-user-attribute-filters`,
        data
      )
      return response.data
    } catch (error) {
      throw error
    }
  }
  return {
    getProductDeliveryById,
    getProductsWithDeliveries,
    getDeliveriesByArrivalDate,
    getArrivalDatesByPeriod,
    getUserAttributeFilters,
    getProductsList,
    getFlourProductById,
    getDeliverySpectralDistance,
    addUserAttributeFilters,
    removeUserAttributeFilters,
  }
}
